import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Phone, Mail, Globe, ArrowRight, Loader2, Star } from "lucide-react";

const QW_DELIVERY = "https://qwilr.imgix.net/WYjvPMK2JNwt6ZFfAif3TsyW49FsLRqD5-3RdQ.jpeg?auto=format&w=1600&q=85";
import { API } from "@/lib/api";
import { initQuoteTracking } from "@/lib/tracker";
import { ytId, venueVideoEntry, deriveQuoteTypeLabel, deriveServices } from "@/lib/quoteContent";
import { AcceptModal } from "@/components/quote/AcceptModal";
import { ExpiredScreen } from "@/components/quote/ExpiredScreen";
import { DeclinedScreen } from "@/components/quote/DeclinedScreen";
import { AcceptedQuoteView } from "@/components/quote/AcceptedQuoteView";
import { DateHoldBanner } from "@/components/quote/QuoteBanners";
import { ReviewsSection } from "@/components/quote/ReviewsSection";
import { GuestbookOffer } from "@/components/quote/GuestbookOffer";
import { QuoteFooter, WhatsAppBubble } from "@/components/quote/QuoteFooter";
import { QuoteHero } from "@/components/quote/QuoteHero";
import { ServicesSection } from "@/components/quote/ServicesSection";
import { InvestmentSection } from "@/components/quote/InvestmentSection";
import { ComparisonSection } from "@/components/quote/ComparisonSection";
import { AppSection } from "@/components/quote/AppSection";
import { VenueVideosSection } from "@/components/quote/VenueVideosSection";
import axios from "axios";

// Original Qwilr imagery
const QW_TEAM = "https://qwilr.imgix.net/mltWzhY1hQ16_cuk9En04EExTD9ATX38FHe5Pw.jpeg?auto=format&w=1200&q=85";
// Ready CTA - abstract/face-less so couples can picture themselves (groom prep detail)
const QW_HERO = "https://images.pexels.com/photos/265721/pexels-photo-265721.jpeg?auto=compress&cs=tinysrgb&w=2400";

const CTA_REVIEWS = [
  {
    quote: "Couldn't fault a single thing about how they captured our wedding day.",
    name: "Chris",
  },
  {
    quote: "It honestly felt like just having more friends at the wedding.",
    name: "Michelle",
  },
];

function IntroSection({ quote }) {
  return (
    <section data-track-section="intro" className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-7 fade-up">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight text-[#1F1E1D] mb-10">
            Dear {quote.partner1_name} &amp; {quote.partner2_name},
          </h2>
          <div className="space-y-6 text-[#5C5A56] leading-relaxed">
            <p>
              Your wedding day will happen once. Everything about it: the
              nerves before you see each other, the music playing as you walk
              in, the way your dad's hands shake as he takes your arm, the
              laugh that turns into tears halfway through your vows. It
              happens once, and then it's gone.
            </p>
            <p>Except it isn't. Not if those memories are captured for a lifetime.</p>
            <p>
              That's what we're here for. Not to direct your day or get in the
              way of it, but to quietly be there while it unfolds exactly as
              it's meant to, so you can be fully present in it, not performing
              for a camera. We watch for the moments most people miss: the
              glance between you two when no one else is looking, the way your
              mum holds it together until she can't, the friend who says the
              thing that makes the whole room lose it. Those are the moments
              you'll want back, years from now.
            </p>
            <p>
              We've been trusted with this since 2011, and it's never stopped
              feeling like a privilege. Every couple is different, so we
              listen first, to your story, your people, the things that matter
              most to you, and we shape everything around that, from our first
              conversation with you right through to the day you sit down and
              watch it back.
            </p>
            <p>
              Because one day, this film won't just be a memory of your
              wedding. It'll be the thing your kids watch to see how you
              looked at each other. It'll be the version of this day you
              return to, again and again, for the rest of your life.
            </p>
            <p>We don't take that lightly. We never will.</p>
          </div>
          <div className="mt-12 pt-8 border-t border-[#E5E3DB] flex items-center gap-5">
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_wedding-pricing-hub/artifacts/xn50zhjr_TEAM%20PHOTOS%2009-08-17%20007.webp"
              alt="Alex Moore"
              data-testid="alex-photo"
              className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-2 border-[#E5E3DB] shrink-0"
            />
            <div>
              <div
                className="font-display text-3xl text-[#2F2E2E] italic mb-1"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Alex Moore
              </div>
              <div className="text-xs tracking-overline uppercase text-[#8A8782] font-medium">
                Founder &amp; Head Videographer
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-5 fade-up delay-1">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img src={QW_TEAM} alt="The Wedding Video Essex team" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}

function ReadyCtaSection() {
  function scrollToPackages() {
    document
      .querySelector("[data-testid='investment-section']")
      ?.scrollIntoView({ behavior: "smooth" });
  }
  return (
    <section data-track-section="ready-cta" className="relative overflow-hidden">
      <img src={QW_HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#21201F]/85" />
      <div className="relative z-10 px-6 md:px-12 py-28 md:py-36 text-center text-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-5xl md:text-7xl tracking-tight leading-none mb-12">
            Ready to get started?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12 text-left" data-testid="cta-reviews">
            {CTA_REVIEWS.map((r) => (
              <figure key={r.name} className="bg-white/10 backdrop-blur-sm border border-white/15 p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={`cta-star-${r.name}-${i}`}
                      className="w-3 h-3 fill-[#CCBA56] text-[#CCBA56]"
                      strokeWidth={1}
                    />
                  ))}
                </div>
                <blockquote className="font-display italic text-lg leading-relaxed text-white/90 mb-3">
                  “{r.quote}”
                </blockquote>
                <figcaption className="text-[10px] tracking-overline uppercase text-white/60 font-medium">
                  {r.name} · Verified on Hitched
                </figcaption>
              </figure>
            ))}
          </div>
          <button
            onClick={scrollToPackages}
            data-testid="secure-date-btn"
            data-track-click="secure-my-date"
            className="inline-flex items-center gap-3 bg-[#CCBA56] hover:bg-[#B5A244] text-[#1F1E1D] px-12 py-5 text-xs tracking-overline uppercase font-medium transition-colors group"
          >
            Secure My Date!
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  );
}

function ViewingWebsiteSection() {
  return (
    <section
      id="viewing-website"
      data-track-section="viewing-website"
      className="px-6 md:px-12 lg:px-24 py-24 md:py-32 scroll-mt-8"
    >
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight text-[#1F1E1D] mb-10">
          Your Viewing &amp; Download Wedding Website...
        </h2>
        <div className="space-y-5 text-[#5C5A56] leading-relaxed mb-10 max-w-2xl mx-auto">
          <p>DVDs and USBs are easy to lose, easy to break, and are very outdated!</p>
          <p>
            With our dedicated delivery platform, you'll receive a personal link
            with your full collection to watch together and share with your
            loved ones again and again.
          </p>
          <p>
            Our digital delivery platform works on every device that connects to
            the internet (and don't worry, we've tested EVERY type of device
            too!).
          </p>
        </div>
        <a
          href="https://weddingvideoessex.co.uk/investment/wedding-video-delivery-platform"
          target="_blank"
          rel="noreferrer"
          data-track-click="delivery-example"
          className="inline-flex items-center gap-2 text-xs tracking-overline uppercase text-[#2F2E2E] hover:text-[#A9927D] font-medium border-b border-[#2F2E2E] hover:border-[#A9927D] pb-1 mb-16"
        >
          See an example!
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </a>
        <div className="w-full aspect-[16/9] overflow-hidden">
          <img src={QW_DELIVERY} alt="Delivery platform" className="w-full h-full object-cover" />
        </div>
      </div>
    </section>
  );
}

function QuestionsSection() {
  return (
    <section data-track-section="questions" className="px-6 md:px-12 py-20 md:py-28 text-center">
      <div className="max-w-4xl mx-auto">
        <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-none text-[#1F1E1D] mb-12">
          Got any questions?
        </h2>
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10 font-display text-xl md:text-2xl text-[#2F2E2E]">
          <a
            href="https://weddingvideo.co.uk"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 hover:text-[#A9927D] transition-colors"
          >
            <Globe className="w-5 h-5" strokeWidth={1.5} />
            weddingvideo.co.uk
          </a>
          <span className="text-[#A9927D] hidden md:inline">|</span>
          <a href="tel:07513011412" className="inline-flex items-center gap-2 hover:text-[#A9927D] transition-colors">
            <Phone className="w-5 h-5" strokeWidth={1.5} />
            07513 011 412
          </a>
          <span className="text-[#A9927D] hidden md:inline">|</span>
          <a
            href="mailto:alex@weddingvideo.co.uk"
            className="inline-flex items-center gap-2 hover:text-[#A9927D] transition-colors"
          >
            <Mail className="w-5 h-5" strokeWidth={1.5} />
            alex@weddingvideo.co.uk
          </a>
        </div>
      </div>
    </section>
  );
}

export default function PublicQuote() {
  const { slug } = useParams();
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPkgId, setSelectedPkgId] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/public/quotes/${slug}`)
      .then((r) => setQuote(r.data))
      .catch((e) =>
        setErr(e?.response?.status === 404 ? "Quote not found" : "Failed to load")
      );
  }, [slug]);

  useEffect(() => {
    if (!quote) return undefined;
    return initQuoteTracking(slug);
  }, [quote, slug]);

  const selectedPackages = useMemo(
    () => (quote?.packages || []).filter((p) => p.selected),
    [quote]
  );
  const quoteTypeLabel = useMemo(() => deriveQuoteTypeLabel(selectedPackages), [selectedPackages]);
  const services = useMemo(() => deriveServices(selectedPackages), [selectedPackages]);

  function onSelect(pkg) {
    setSelectedPkgId(pkg.id);
    setModalOpen(true);
  }

  if (err)
    return (
      <div className="min-h-screen flex items-center justify-center text-[#5C5A56] font-display text-3xl">
        {err}
      </div>
    );
  if (!quote)
    return (
      <div className="min-h-screen flex items-center justify-center text-[#5C5A56]">
        <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} />
      </div>
    );

  const partnerNames = `${quote.partner1_name} & ${quote.partner2_name}`;
  const venueVideoIds = (
    quote.venue_video_urls?.length ? quote.venue_video_urls : [quote.venue_video_url]
  )
    .map(venueVideoEntry)
    .filter(Boolean)
    .slice(0, 3);
  const noExpiry = quote.expiry_type === "no_expiry";
  const holdDeadline = noExpiry
    ? Infinity
    : new Date(
        quote.hold_expires_at || new Date(quote.created_at).getTime() + 7 * 86400000
      ).getTime();

  if (quote.status === "declined") {
    return <DeclinedScreen slug={slug} />;
  }

  if (quote.status === "accepted") {
    return <AcceptedQuoteView quote={quote} />;
  }

  if (quote.status !== "accepted" && Date.now() > holdDeadline) {
    return <ExpiredScreen slug={slug} />;
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="public-quote-page">
      {!noExpiry && <DateHoldBanner deadline={holdDeadline} />}
      <QuoteHero quote={quote} quoteTypeLabel={quoteTypeLabel} />
      <IntroSection quote={quote} />
      {quote.venue_name && venueVideoIds.length > 0 && (
        <VenueVideosSection venueName={quote.venue_name} videoIds={venueVideoIds} />
      )}
      <ComparisonSection venueName={quote.venue_name} slug={slug} />
      <InvestmentSection quote={quote} selectedPackages={selectedPackages} onSelect={onSelect} />
      <ServicesSection services={services} />

      <ReviewsSection />
      <AppSection />
      <ReadyCtaSection />
      <ViewingWebsiteSection />
      {quote.video_booth_offer && <GuestbookOffer holdDeadline={holdDeadline} />}
      <QuestionsSection />
      <QuoteFooter partnerNames={partnerNames} />

      <AcceptModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        quote={quote}
        selectedPkgId={selectedPkgId}
      />

      <WhatsAppBubble />
    </div>
  );
}
